import { useGetUserByIdQuery } from "../../api/userAPI";
import { useAppSelector } from "../../store/hooks";
import { selectCurrentUser } from "../../store/selectors/authSelectors";

/**
 * spec-42 — the ONLY way to read the logged-in user's OWN server profile.
 *
 * TWO guards, and both are load-bearing. Do not "simplify" either one away:
 *
 * 1. `currentData`, NEVER `data`. RTK Query's `data` falls back to a component-local `lastResult`
 *    ref whenever the CURRENT arg has no successful result (`queryStatePreSelector`), and that ref
 *    is discarded ONLY when the serialized args MATCH. Across an account switch the args change
 *    ({Id: A} -> {Id: B}), so the ref keeps serving the PREVIOUS user's profile — and because it is
 *    component state, not store state, it survives `jumboMediaAPI.util.resetApiState()` too.
 *    `currentData` is "the result for the current arg" and is simply `undefined` mid-transition,
 *    which is the correct answer.
 * 2. The id check. Even a correct cache can be raced; the profile is only the current user's if it
 *    says so. This is the check that makes the whole class of bug a no-op regardless of what RTK
 *    returns. `Id` is confirmed present on the UserGet response — it is the value that produced the
 *    previous user's guid in the failing `PUT /api/Users/UserPut/<id>` URL.
 *
 * Returns `profile: undefined` while switching. That is CORRECT — callers must render blank and
 * block their save, never fall back to the previous user's values (D4). Two of the five consumers
 * (LangSelect, UserSettings) used to PUT the previous account's name/email/phone onto the current
 * user's row and get a 200 back; blank is recoverable, a silent wrong identity is not.
 *
 * `data` is deliberately NOT re-exported: the stale value must not be reachable from here.
 */
export const useCurrentUserProfile = () => {
  const user = useAppSelector(selectCurrentUser);
  const userId = user?.userId;

  const {
    currentData,
    isFetching,
    isLoading,
    isSuccess,
    isError,
    error,
    refetch,
  } = useGetUserByIdQuery({ Id: userId }, { skip: !userId });

  const profile =
    currentData && currentData.Id === userId ? currentData : undefined;

  return {
    profile,
    /** The session id the profile was validated against — same source as the guard above. */
    userId,
    isFetching,
    isLoading,
    isSuccess,
    isError,
    error,
    refetch,
  };
};

export default useCurrentUserProfile;
